import React, { useMemo, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X, AlertTriangle, Building2, User, Calendar, Mail, FileText, Package,
  Copy, Code, Share2, Check, Layers, FileDown,
} from 'lucide-react';
import { poToJSON, poToMarkdown, poToPlainText } from '@techstack/ui';
import apiClient from '../../lib/apiClient';
import logger from '../../lib/logger';
import ClassificationChip from '../ClassificationChip';
import { ClassificationAgentTrigger } from '../ClassificationAgent';
import './PoDetailModal.css';
import OverlayPortal from '../shared/OverlayPortal';
import useModalBehavior from '../../hooks/useModalBehavior';

/*
 * PoDetailModal — global, app-wide PO viewer.
 *
 * Renders a single Purchase Order in a document-style layout: header block
 * (department, supplier, buyer, status, term), line-items table, grand total
 * roll-up. Designed so any page in the app can drop in a <PoLink> next to a
 * PO number and get this modal for free.
 *
 * Backend: GET /api/po/:documentNumber (routes/po.js, gated admin/cisco_user).
 *
 * Theme: every color uses CSS variables defined in src/styles/variables.css
 * with dark-mode overrides. The "paper" surface is var(--color-surface), the
 * stamp accents use color-mix on var(--color-primary).
 */

function formatCurrency(n) {
  const v = Number(n) || 0;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Math.round(v).toLocaleString()}`;
}

function formatCurrencyExact(n) {
  const v = Number(n) || 0;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function PoDetailModal({ documentNumber, departmentId: initialDepartmentId, startDate, isOpen = true, onClose }) {
  /*
   * This was a modal in appearance only: a bare `<div>` over the page with no
   * `role`, no `aria-modal`, no accessible name, no Escape handler and no
   * focus management. A screen reader was told nothing had happened, and a
   * keyboard reader who opened a PO from inside the transactions drill kept
   * their focus on the `.po-link` underneath it — measured live.
   *
   * It is also the INNER modal of the only stack on the account board, which
   * is what forced the topmost-only rule into the hook: the PO detail renders
   * inside the drill's own React subtree, so one Escape used to close the PO,
   * the drill beneath it, and return the reader to the board.
   *
   * `isOpen` defaults to true and `PoLink` mounts this conditionally, but two
   * of the eleven call sites pass `isOpen` as state, so the guard has to
   * match the early return below rather than assume mounting means open.
   */
  const modal = useModalBehavior(onClose, { enabled: isOpen && !!documentNumber });
  const queryClient = useQueryClient();
  const [showRaw, setShowRaw] = useState(false);
  const [copyState, setCopyState] = useState({ text: false, markdown: false, json: false, share: false });
  // CA purchase_document_num isn't unique — same PO# can exist independently
  // across departments. If the caller didn't pass departmentId AND the
  // result spans multiple depts, the modal lets the user pick one and
  // re-fetches scoped to it.
  //
  // Additionally, PO numbers can be REUSED over time within the same dept
  // (e.g. CARB's PO# 0000002044 was used in 2018 AND again in 2025). When
  // the caller passes startDate, the backend filters to rows within ±18mo
  // of that date to show only the relevant "generation" of the PO.
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(initialDepartmentId ?? null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['po-detail', documentNumber, selectedDepartmentId, startDate],
    queryFn: async () => {
      const t0 = performance.now();
      const params = new URLSearchParams();
      if (selectedDepartmentId) params.set('department_id', selectedDepartmentId);
      if (startDate) {
        // Handle various date formats: Date object, ISO string, or date-only string
        let dateStr;
        if (typeof startDate === 'string') {
          // Extract just the date part if it's an ISO timestamp
          dateStr = startDate.split('T')[0];
        } else if (startDate instanceof Date) {
          dateStr = startDate.toISOString().split('T')[0];
        } else {
          dateStr = String(startDate).split('T')[0];
        }
        params.set('start_date', dateStr);
      }
      const qs = params.toString();
      const url = `/api/po/${encodeURIComponent(documentNumber)}${qs ? `?${qs}` : ''}`;
      logger.api.info('PoDetailModal:fetch:start', {
        document_number: documentNumber, department_id: selectedDepartmentId, start_date: startDate,
      });
      try {
        const result = await apiClient.get(url);
        logger.api.info('PoDetailModal:fetch:ok', {
          document_number: documentNumber,
          department_id: selectedDepartmentId,
          duration_ms: Math.round(performance.now() - t0),
          line_count: result?.totals?.line_count ?? 0,
          distinct_department_count: result?.totals?.distinct_department_count ?? 0,
        });
        return result;
      } catch (err) {
        logger.api.error('PoDetailModal:fetch:failed', {
          document_number: documentNumber,
          department_id: selectedDepartmentId,
          duration_ms: Math.round(performance.now() - t0),
          status: err?.status,
        }, err);
        throw err;
      }
    },
    enabled: isOpen && !!documentNumber,
  });

  // Collapse rows that look like the same physical line item ingested twice
  // (almost always the CSV-import path AND the scraper path picking up the
  // same PO independently — see backend ingestion notes). Group key is
  // (line, description normalized) — amount is NOT in the key because the
  // two ingestions sometimes record different financial views of the same
  // physical line (e.g. CSV captures an aggregated total, scraper captures
  // unit_price * qty after detail extraction). Treating those as separate
  // rows would double-count spend.
  //
  // Empty placeholder rows ("Row N - No Description" with $0) are dropped.
  //
  // For each group we pick a canonical row by descending priority:
  //   1. Most metadata populated (term + qty/unit + UNSPSC are all signals
  //      that the row came from a richer ingestion path)
  //   2. Highest component_confidence
  //   3. Highest amount (best guess at the true line value)
  //   4. Lowest id (earliest record)
  //
  // When amounts disagree across the group we surface a warning chip so
  // the user knows the canonical amount is one of N divergent values.
  const dedupedLines = useMemo(() => {
    const rawLines = data?.lines || [];
    if (rawLines.length === 0) return { unique: [], droppedPlaceholders: 0, mergedRowCount: 0 };

    let droppedPlaceholders = 0;
    const realLines = rawLines.filter(l => {
      const isPlaceholder = (l.amount === 0 || l.amount == null)
        && /^row \d+ - no description$/i.test((l.description || '').trim());
      if (isPlaceholder) droppedPlaceholders += 1;
      return !isPlaceholder;
    });

    const groups = new Map();
    for (const l of realLines) {
      const key = [
        String(l.line ?? ''),
        (l.description || '').trim().slice(0, 200).toLowerCase(),
      ].join('|');
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(l);
    }

    // metadata score = number of fields the row has populated. Higher = more
    // detail captured (likely the post-extraction scraper row, which is
    // canonical for term/qty/UNSPSC info even when the CSV has a higher amt).
    const metaScore = (l) => (
      (l.start_date ? 1 : 0) +
      (l.end_date ? 1 : 0) +
      (l.quantity != null ? 1 : 0) +
      (l.unit_price != null ? 1 : 0) +
      (l.uom ? 1 : 0) +
      (l.unspsc_code ? 1 : 0)
    );

    const unique = [];
    let mergedRowCount = 0;
    for (const [, members] of groups) {
      members.sort((a, b) => {
        const m = metaScore(b) - metaScore(a);
        if (m !== 0) return m;
        const c = (b.component_confidence ?? 0) - (a.component_confidence ?? 0);
        if (c !== 0) return c;
        const amt = (b.amount ?? 0) - (a.amount ?? 0);
        if (amt !== 0) return amt;
        return (a.id ?? 0) - (b.id ?? 0);
      });
      const canonical = members[0];
      const others = members.slice(1);
      mergedRowCount += others.length;

      const amounts = members.map(m => Number(m.amount) || 0);
      const minAmount = Math.min(...amounts);
      const maxAmount = Math.max(...amounts);
      // Treat as "amounts disagree" when the spread is more than $0.01 — this
      // is the signal we render as a warning chip on the row.
      const amountsDiffer = members.length > 1 && (maxAmount - minAmount) > 0.01;

      unique.push({
        ...canonical,
        merge_count: members.length,
        merged_methods: [...new Set(members.map(m => m.classification_method).filter(Boolean))],
        merged_amount_min: minAmount,
        merged_amount_max: maxAmount,
        merged_amounts_differ: amountsDiffer,
      });
    }
    unique.sort((a, b) => {
      const al = String(a.line ?? '');
      const bl = String(b.line ?? '');
      return al.localeCompare(bl, undefined, { numeric: true });
    });
    return { unique, droppedPlaceholders, mergedRowCount };
  }, [data]);

  const dedupTotal = useMemo(
    () => dedupedLines.unique.reduce((s, l) => s + (Number(l.amount) || 0), 0),
    [dedupedLines]
  );

  // Transform backend data into shared PoDetail interface
  const poDetailPayload = useMemo(() => {
    if (!data) return null;
    const linesToUse = showRaw ? (data.lines || []) : dedupedLines.unique;
    return {
      header: {
        document_number: data.header.purchase_document_num,
        agency: data.header.agency,
        official_name: data.header.official_name,
        department_name: data.header.department_name,
        bu_code: data.header.bu_code,
        supplier_name: data.header.supplier_name,
        supplier_number: data.header.supplier_number,
        buyer: data.header.buyer,
        buyer_email: data.header.buyer_email,
        purchase_order_date: data.header.purchase_order_date,
        po_status: data.header.po_status,
        acquisition_type: data.header.acquisition_type,
        acquisition_method: data.header.acquisition_method,
        acquisition_sub_type: data.header.acquisition_sub_type,
        lpa_contract_id: data.header.lpa_contract_id,
        data_source: data.header.data_source,
      },
      totals: {
        total_spend: showRaw ? data.totals.total_spend : dedupTotal,
        line_count: linesToUse.length,
        earliest_start: data.totals.earliest_start,
        latest_end: data.totals.latest_end,
        distinct_classified_vendors: data.totals.distinct_classified_vendors,
      },
      lines: linesToUse.map(l => ({
        line: l.line,
        description: l.description || l.item_description,
        component_name: l.component_name,
        classified_vendor: l.classified_vendor,
        component_confidence: l.component_confidence,
        unspsc_code: l.unspsc_code,
        unspsc_description: l.unspsc_description,
        quantity: l.quantity,
        uom: l.uom,
        unit_price: l.unit_price,
        amount: l.amount || 0,
        start_date: l.start_date,
        end_date: l.end_date,
      })),
    };
  }, [data, showRaw, dedupedLines, dedupTotal]);

  const handleCopyText = useCallback(async () => {
    if (!poDetailPayload) return;
    try {
      await navigator.clipboard.writeText(poToPlainText(poDetailPayload));
      setCopyState(s => ({ ...s, text: true }));
      setTimeout(() => setCopyState(s => ({ ...s, text: false })), 1500);
      logger.ui.info('PoDetailModal:copy-text', { document_number: documentNumber });
    } catch (err) {
      logger.api.error('PoDetailModal:copy-text:failed', { document_number: documentNumber }, err);
    }
  }, [poDetailPayload, documentNumber]);

  const handleCopyMarkdown = useCallback(async () => {
    if (!poDetailPayload) return;
    try {
      await navigator.clipboard.writeText(poToMarkdown(poDetailPayload));
      setCopyState(s => ({ ...s, markdown: true }));
      setTimeout(() => setCopyState(s => ({ ...s, markdown: false })), 1500);
      logger.ui.info('PoDetailModal:copy-markdown', { document_number: documentNumber });
    } catch (err) {
      logger.api.error('PoDetailModal:copy-markdown:failed', { document_number: documentNumber }, err);
    }
  }, [poDetailPayload, documentNumber]);

  const handleCopyJson = useCallback(async () => {
    if (!poDetailPayload) return;
    try {
      await navigator.clipboard.writeText(poToJSON(poDetailPayload));
      setCopyState(s => ({ ...s, json: true }));
      setTimeout(() => setCopyState(s => ({ ...s, json: false })), 1500);
      logger.ui.info('PoDetailModal:copy-json', { document_number: documentNumber });
    } catch (err) {
      logger.api.error('PoDetailModal:copy-json:failed', { document_number: documentNumber }, err);
    }
  }, [poDetailPayload, documentNumber]);

  const handleShare = useCallback(async () => {
    if (!data) return;
    const shareData = {
      title: `PO ${data.header.purchase_document_num}`,
      text: `${data.header.supplier_name || 'Vendor'} → ${data.header.agency || 'Department'} · ${formatCurrencyExact(dedupTotal)} (${dedupedLines.unique.length} line items)`,
      url: window.location.href,
    };
    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy a sharable summary to clipboard.
        await navigator.clipboard.writeText(`${shareData.title}\n${shareData.text}\n${shareData.url}`);
      }
      setCopyState(s => ({ ...s, share: true }));
      setTimeout(() => setCopyState(s => ({ ...s, share: false })), 1500);
      logger.ui.info('PoDetailModal:share', { document_number: documentNumber });
    } catch (err) {
      // User-cancelled share is not an error.
      if (err.name !== 'AbortError') {
        logger.api.error('PoDetailModal:share:failed', { document_number: documentNumber }, err);
      }
    }
  }, [data, dedupedLines, dedupTotal, documentNumber]);

  if (!isOpen || !documentNumber) return null;

  const header = data?.header;
  const totals = data?.totals;
  const linesToRender = showRaw ? (data?.lines || []) : dedupedLines.unique;
  const totalToShow = showRaw ? (totals?.total_spend ?? 0) : dedupTotal;
  const inconsistencies = totals?.header_inconsistencies || [];
  const hasDedup = dedupedLines.mergedRowCount > 0 || dedupedLines.droppedPlaceholders > 0;

  return (
    <OverlayPortal>
    <div className="po-modal-overlay" {...modal.overlayProps}>
      <div
        className="po-modal-paper"
        {...modal.dialogProps}
        aria-label={`Purchase order ${documentNumber}`}
      >
        <div className="po-modal-toolbar">
          {data && (
            <>
              <button
                className="po-modal-tool"
                onClick={handleCopyText}
                title="Copy as plain text"
              >
                {copyState.text ? <Check size={14} /> : <Copy size={14} />}
                <span>{copyState.text ? 'Copied' : 'Plain'}</span>
              </button>
              <button
                className="po-modal-tool"
                onClick={handleCopyMarkdown}
                title="Copy as Markdown (for Claude, ChatGPT, docs)"
              >
                {copyState.markdown ? <Check size={14} /> : <FileDown size={14} />}
                <span>{copyState.markdown ? 'Copied' : 'Markdown'}</span>
              </button>
              <button
                className="po-modal-tool"
                onClick={handleCopyJson}
                title="Copy as JSON (for programmatic use)"
              >
                {copyState.json ? <Check size={14} /> : <Code size={14} />}
                <span>{copyState.json ? 'Copied' : 'JSON'}</span>
              </button>
              <button
                className="po-modal-tool"
                onClick={handleShare}
                title="Share via system share sheet (or copy summary)"
              >
                {copyState.share ? <Check size={14} /> : <Share2 size={14} />}
                <span>{copyState.share ? 'Shared' : 'Share'}</span>
              </button>
            </>
          )}
          <button className="po-modal-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {isLoading && <div className="po-modal-loading">Loading PO {documentNumber}…</div>}

        {error && (
          <div className="po-modal-error">
            <AlertTriangle size={18} />
            <div>
              <strong>Failed to load PO {documentNumber}</strong>
              <p>{error.message}</p>
            </div>
          </div>
        )}

        {data && (
          <>
            <div className="po-doc-stamp">PURCHASE ORDER</div>

            <header className="po-doc-header">
              <div className="po-doc-title">
                <h1>#{header.purchase_document_num}</h1>
                {header.po_status && (
                  <span className="po-status">{header.po_status}</span>
                )}
              </div>
              <div className="po-doc-summary">
                <div className="po-doc-summary__label">Lifetime contract value</div>
                <div className="po-doc-summary__value">{formatCurrencyExact(totalToShow)}</div>
                <div className="po-doc-summary__sub">
                  {linesToRender.length} line item{linesToRender.length === 1 ? '' : 's'}
                  {hasDedup && !showRaw &&
                    ` · deduplicated from ${totals.line_count} raw rows`}
                  {totals.earliest_start && totals.latest_end &&
                    ` · ${formatDate(totals.earliest_start)} – ${formatDate(totals.latest_end)}`}
                </div>
              </div>
            </header>

            <section className="po-doc-meta">
              <div className="po-doc-meta__grid">
                <PoMetaField icon={<Building2 size={14} />} label="Department">
                  {header.agency || header.department_name || '—'}
                  {header.bu_code && <span className="po-meta-sub"> · BU {header.bu_code}</span>}
                </PoMetaField>
                <PoMetaField icon={<Package size={14} />} label="Supplier">
                  {header.supplier_name || '—'}
                  {header.supplier_number && <span className="po-meta-sub"> · #{header.supplier_number}</span>}
                </PoMetaField>
                <PoMetaField icon={<User size={14} />} label="Buyer">
                  {header.buyer || '—'}
                </PoMetaField>
                {header.buyer_email && (
                  <PoMetaField icon={<Mail size={14} />} label="Buyer email">
                    <a href={`mailto:${header.buyer_email}`}>{header.buyer_email}</a>
                  </PoMetaField>
                )}
                <PoMetaField icon={<Calendar size={14} />} label="PO date">
                  {formatDate(header.purchase_order_date)}
                </PoMetaField>
                <PoMetaField icon={<FileText size={14} />} label="Acquisition type">
                  {header.acquisition_type || '—'}
                  {header.acquisition_sub_type && <span className="po-meta-sub"> · {header.acquisition_sub_type}</span>}
                </PoMetaField>
                {header.acquisition_method && (
                  <PoMetaField icon={<FileText size={14} />} label="Method">
                    {header.acquisition_method}
                  </PoMetaField>
                )}
                {header.lpa_contract_id && (
                  <PoMetaField icon={<FileText size={14} />} label="LPA contract">
                    {header.lpa_contract_id}
                  </PoMetaField>
                )}
              </div>

              {totals.distinct_department_count > 1 && !selectedDepartmentId && (
                <div className="po-doc-warn">
                  <AlertTriangle size={14} />
                  <div>
                    <strong>This PO number exists in {totals.distinct_department_count} different departments.</strong>
                    {' '}California `purchase_document_num` is per-agency, so these are
                    {' '}{totals.distinct_department_count} independent purchase orders that happen to share a number string. Pick one to view in isolation:
                    <div className="po-doc-warn__chooser">
                      {totals.department_breakdown.map(d => (
                        <button
                          key={d.department_id}
                          type="button"
                          className="po-doc-warn__chooser-btn"
                          onClick={() => setSelectedDepartmentId(d.department_id)}
                        >
                          <strong>{d.agency || `Dept ${d.department_id}`}</strong>
                          <span> · {d.line_count} line{d.line_count === 1 ? '' : 's'} · {formatCurrencyExact(d.total_spend)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {totals.distinct_classified_vendors.length > 1 && (
                <div className="po-doc-warn">
                  <AlertTriangle size={14} />
                  Mixed vendor classifications on this PO:
                  {' '}{totals.distinct_classified_vendors.join(', ')}.
                  {totals.distinct_department_count > 1
                    ? ' This is expected when a PO# spans multiple departments — pick a department above to see one PO at a time.'
                    : ' Likely a misclassification — only one vendor should normally appear on a single PO.'}
                </div>
              )}
              {inconsistencies.length > 0 && (
                <div className="po-doc-warn">
                  <AlertTriangle size={14} />
                  Header values disagree across lines on fields: {inconsistencies.join(', ')}.
                </div>
              )}
              {hasDedup && (
                <div className="po-doc-info">
                  <Layers size={14} />
                  <div>
                    <strong>{totals.line_count} raw rows in DB → {dedupedLines.unique.length} unique line items.</strong>
                    {dedupedLines.mergedRowCount > 0 && (
                      <> Collapsed {dedupedLines.mergedRowCount} duplicate row{dedupedLines.mergedRowCount === 1 ? '' : 's'} (same line # + same description; amounts kept on the canonical row).</>
                    )}
                    {dedupedLines.droppedPlaceholders > 0 && (
                      <> Dropped {dedupedLines.droppedPlaceholders} empty placeholder row{dedupedLines.droppedPlaceholders === 1 ? '' : 's'}.</>
                    )}
                    {' '}This usually happens when the CSV import path and the SCPRS scraper both ingest the same PO. The classifier runs on each row independently, so each duplicate may carry a different <code>classification_method</code>.
                    <button
                      className="po-doc-info__toggle"
                      onClick={() => setShowRaw(s => !s)}
                    >
                      {showRaw ? 'Hide raw rows' : `Show all ${totals.line_count} raw rows`}
                    </button>
                  </div>
                </div>
              )}
            </section>

            <section className="po-doc-lines">
              <h2>Line items</h2>
              <table className="po-line-table">
                <thead>
                  <tr>
                    <th className="po-col-num">#</th>
                    <th>Description</th>
                    <th className="po-col-class">Classification</th>
                    <th className="po-agent-col" aria-label="Agent"></th>
                    <th className="po-col-term">Term</th>
                    <th className="po-num po-col-qty">Qty</th>
                    <th className="po-num po-col-unit">Unit</th>
                    <th className="po-num po-col-amount">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {linesToRender
                    .slice()
                    .sort((a, b) => {
                      // Numeric-aware sort so "1, 2, 3, 10, 11" rather than
                      // "1, 10, 11, 2, 3". purchase_document_line is text in
                      // the DB ("1 line", "2 lines") but typically a leading
                      // integer.
                      const al = String(a.line ?? '');
                      const bl = String(b.line ?? '');
                      return al.localeCompare(bl, undefined, { numeric: true, sensitivity: 'base' });
                    })
                    .map((l, i) => (
                    <tr key={l.id}>
                      <td className="po-line-num">{l.line || (i + 1)}</td>
                      <td className="po-line-desc">
                        {l.description || '—'}
                        {(l.unspsc_code || l.classification_method) && (
                          <div className="po-line-meta">
                            {l.unspsc_code && <span>UNSPSC {l.unspsc_code}</span>}
                            {l.classification_method && <span>{l.classification_method}</span>}
                            {l.component_confidence != null && (
                              <span>conf {(l.component_confidence * 100).toFixed(0)}%</span>
                            )}
                            {l.merge_count > 1 && (
                              <span title={`Merged from ${l.merge_count} duplicate DB rows: ${(l.merged_methods || []).join(' + ')}`}>
                                merged ×{l.merge_count}
                              </span>
                            )}
                            {l.merged_amounts_differ && (
                              <span
                                style={{
                                  background: 'color-mix(in srgb, var(--color-warning) 18%, transparent)',
                                  color: 'var(--color-warning)',
                                  borderColor: 'color-mix(in srgb, var(--color-warning) 35%, transparent)',
                                }}
                                title={`Duplicate rows recorded different amounts: ${formatCurrencyExact(l.merged_amount_min)} – ${formatCurrencyExact(l.merged_amount_max)}. Showing the canonical row's value (most metadata-rich).`}
                              >
                                amounts vary {formatCurrency(l.merged_amount_min)}–{formatCurrency(l.merged_amount_max)}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {l.id ? (
                          <ClassificationChip
                            itemId={l.id}
                            componentName={l.component_name}
                            confidence={l.component_confidence}
                            classificationMethod={l.classification_method}
                            onUpdated={() => queryClient.invalidateQueries({
                              queryKey: ['po-detail', documentNumber],
                            })}
                          />
                        ) : (
                          l.component_name || '—'
                        )}
                        {l.classified_vendor && (
                          <div className="po-meta-sub">{l.classified_vendor}</div>
                        )}
                      </td>
                      <td className="po-agent-col">
                        {l.id && (
                          <ClassificationAgentTrigger
                            itemId={l.id}
                            description={l.item_description}
                            supplier={data.header?.supplier_name}
                            amount={l.amount}
                            componentName={l.component_name}
                            componentId={l.tech_component_id}
                            confidence={l.component_confidence}
                            method={l.classification_method}
                            documentNumber={documentNumber}
                            departmentId={selectedDepartmentId || l.department_id}
                            source="po_detail"
                            variant="icon"
                          />
                        )}
                      </td>
                      <td className="po-line-term">
                        {l.start_date && l.end_date
                          ? <>{formatDate(l.start_date)}<br />– {formatDate(l.end_date)}</>
                          : '—'}
                      </td>
                      <td className="po-num">{l.quantity ?? '—'}{l.uom ? ` ${l.uom}` : ''}</td>
                      <td className="po-num">{l.unit_price != null ? formatCurrency(l.unit_price) : '—'}</td>
                      <td className="po-num po-line-amount">{formatCurrencyExact(l.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={7} className="po-num"><strong>Grand total</strong></td>
                    <td className="po-num"><strong>{formatCurrencyExact(totalToShow)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </section>

            <footer className="po-doc-footer">
              Source: <code>{header.data_source || 'csv'}</code>
              {' · '}Note: California multi-year service contracts are commonly
              booked as one annual installment line per fiscal year. Lines
              with identical descriptions and amounts but different start
              dates are not duplicates — they are separate annual obligations
              of the same contract.
            </footer>
          </>
        )}
      </div>
    </div>
    </OverlayPortal>
  );
}

function PoMetaField({ icon, label, children }) {
  return (
    <div className="po-meta-field">
      <div className="po-meta-label">{icon}<span>{label}</span></div>
      <div className="po-meta-value">{children}</div>
    </div>
  );
}

export default PoDetailModal;
